import { useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';

import formatTime from '@cartwhl/common/utils/formatTime';
import Text from '@cartwhl/ui/base/Text';
import TrimIcon from '@cartwhl/ui/icons/TrimIcon';
import Button from '@cartwhl/ui/input/Button';

import { HelpMenu } from 'components/StudioLayout';
import {
  animationGenerationStore,
  animationPlayerStore,
  sceneSettingsStore,
  userStore,
} from 'stores';

import { formatFrames } from '../utils';
import MainPlayerControls from './MainPlayerControls';
import LegacyTimeline from './Timeline';
import TrimControls from './TrimControls';

export default function AnimationFooter() {
  const { jobData } = useSnapshot(animationGenerationStore.state);
  const { userInfo } = useSnapshot(userStore.state);
  const isNewProject = !jobData;
  const isUserOwned = userInfo?.userID === jobData?.createdBy;

  const playerStore = animationPlayerStore.defaultInstance;
  const { controls, currentFrame, duration } = useSnapshot(playerStore.state);
  const {
    playbackSettings: { frameRate },
  } = useSnapshot(sceneSettingsStore.state);
  const [showTime, setShowTime] = useState(false);

  const displayString = useMemo(() => {
    if (showTime) {
      const frameTime = 1 / frameRate;
      const timeDisplay = `${formatTime(currentFrame * frameTime)}/${formatTime(duration.seconds)}s`;
      return timeDisplay;
    }
    const frameDisplay = `${formatFrames(currentFrame)}/${formatFrames(duration.frames - 1)}`;
    return frameDisplay;
  }, [showTime, frameRate, currentFrame, duration.seconds, duration.frames]);

  return (
    <div className="px-2 lg:px-0">
      {!isNewProject && isUserOwned && (
        <div className="relative z-0 flex h-[44px] flex-none flex-col justify-end pt-5">
          {controls === 'player' && (
            <LegacyTimeline animationPlayerStore={playerStore} hideCount />
          )}
          {controls === 'trim' && (
            <div className="relative top-1.5">
              <TrimControls
                animationPlayerStore={playerStore}
                showPlayButton={false}
              />
            </div>
          )}
        </div>
      )}
      <div className="flex h-[64px] flex-none">
        <div className="flex flex-1 items-center">
          {!isNewProject && (
            <>
              <button
                onClick={() => setShowTime(!showTime)}
                className="hidden lg:inline-block"
              >
                <Text
                  as="div"
                  variant="studio/body/medium/sm"
                  className="text-gray-60 dark:text-gray-45 mr-2 -ml-2 min-w-[63px] text-end transition-all duration-100"
                  style={{
                    width: showTime
                      ? duration.seconds.toString().length * 5
                      : duration.frames.toString().length * 4,
                  }}
                >
                  {displayString}
                </Text>
              </button>
              {controls === 'player' && (
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<TrimIcon />}
                  onClick={() => playerStore.setControls('trim')}
                >
                  Trim
                </Button>
              )}
            </>
          )}
        </div>
        {!isNewProject && (
          <MainPlayerControls animationPlayerStore={playerStore} />
        )}
        <div className="flex flex-1 items-center justify-end">
          <HelpMenu />
        </div>
      </div>
    </div>
  );
}
